import React, { useState, useEffect } from 'react';
import './VisualUniverseDialog.css';
import dayjs from 'dayjs'; // ADDED: for default date values


import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, Button, Box, Typography, TextField, Select, MenuItem, FormControl, FormControlLabel, Checkbox, Radio, RadioGroup } from '@mui/material';
import { readOnlyFieldSx, floatingPanelSx, panelDividerColor } from '../utils/panelSurfaces';
import {
  findAspects, aspectLabel, aspectLabelParts, configurationAspects,
  anglesForSet, ASPECT_SETS, clampOrb, defaultOrbForSet, MAX_ORB, ORB_MIN, ORB_MAX,
} from '../utils/visualUniverseAspects';
import {
  SIGN_LINE_COORDS, CHART_WIDTH, CHART_HEIGHT,
  cellDividers, cellKeyFor, homeCellFor, containDotX,
} from '../utils/visualUniverseGeometry';
import HelpIcon from './HelpIcon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Tooltip from '@mui/material/Tooltip';



// --- ChartSVG: Top-level component for chart rendering ---
// --- BEGIN TEMPORARY COORDINATE CAPTURE ---
/**
 * One labelled, outlined cluster of header controls.
 *
 * Exists so the header reads as a set of separate questions rather than a row of eight controls.
 * The caption is the question; the border is the boundary. Without it the two radio groups sit
 * adjacent and read as one list of four mutually exclusive options — which inverts their meaning,
 * since centre and zodiac are independent and combine into four frames.
 */
const HeaderGroup = ({ label, children }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid rgba(0,0,0,0.18)',
      borderRadius: 1.5,
      px: 1.25,
      pt: 0.25,
      pb: 0.25,
      minWidth: 0,
    }}
  >
    <Typography
      variant="caption"
      sx={{ fontWeight: 700, letterSpacing: 0.4, opacity: 0.65, lineHeight: 1.2, whiteSpace: 'nowrap' }}
    >
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
      {children}
    </Box>
  </Box>
);

HeaderGroup.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

/**
 * One colour per aspect, so the wheel is readable when several are drawn at once.
 *
 * 90 and 270 share a colour deliberately: they are the same aspect - a square - seen from either
 * body. The angle in the label is what distinguishes them, and giving them separate hues would
 * suggest two different relationships.
 */
const ASPECT_COLORS = {
  0: '#c62828',    // conjunction
  45: '#ef6c00',   // semi-square — an eighth, so a warmer shade of the square's family
  90: '#1565c0',   // square
  120: '#2e7d32',  // trine
  135: '#00838f',  // sesquiquadrate — three eighths
  180: '#6a1b9a',  // opposition
  270: '#1565c0',  // square, the other way round
};

const ChartSVG = ({ showBackground = true, signTextLines = null, signTooltips = [], aspects = [],
                   hoveredAspect = null, onHoverAspect = null }) => {
    const width = CHART_WIDTH;
    const height = CHART_HEIGHT;
    const topLeft = { x: 0, y: 0 };
    const topRight = { x: width, y: 0 };
    const bottomLeft = { x: 0, y: height };
    const bottomRight = { x: width, y: height };
    const topCenter = { x: width / 2, y: 0 };
    const rightCenter = { x: width, y: height / 2 };
    const bottomCenter = { x: width / 2, y: height };
    const leftCenter = { x: 0, y: height / 2 };
    const center = { x: width / 2, y: height / 2 };
    // Define sign positions
    const signPositions = [
      { sign: 'ARIES',       x: width * 0.45, y: height * 0.25 },
      { sign: 'TAURUS',      x: width * 0.20, y: height * 0.10 },
      { sign: 'GEMINI',      x: width * 0.05, y: height * 0.25 },
      { sign: 'CANCER',      x: width * 0.20, y: height * 0.50 },
      { sign: 'LEO',         x: width * 0.05, y: height * 0.75 },
      { sign: 'VIRGO',       x: width * 0.20, y: height * 0.90 },
      { sign: 'LIBRA',       x: width * 0.45, y: height * 0.75 },
      { sign: 'SCORPIO',     x: width * 0.70, y: height * 0.90 },
      { sign: 'SAGITTARIUS', x: width * 0.85 - 1.5 * 72, y: height * 0.75 },
      { sign: 'CAPRICORN',   x: width * 0.65, y: height * 0.50 },
      { sign: 'AQUARIUS',    x: width * 0.85, y: height * 0.25 },
      { sign: 'PISCES',      x: width * 0.70, y: height * 0.10 },
    ];

    /**
     * Where the magenta dot sits for a sign.
     *
     * <p>Derived by the SAME arithmetic renderSignTextLines uses to place it — first coordinate of
     * the sign, offset past the width of its first text line — rather than by an independent
     * guess. Two expressions for one position drift the moment either is touched, and an aspect
     * line ending a few pixels off its dot reads as a rendering fault.
     *
     * <p>`occupied` says whether the house actually holds a planet. The dot itself is drawn for
     * every sign, but an aspect line may only terminate on an occupied one — a line into an empty
     * house has no planet at its end to be about.
     */
    const firstLineLength = (signIdx) => {
      const line = (signTextLines && signTextLines[signIdx] && signTextLines[signIdx][0]) || '';
      return line.replace(/<[^>]+>/g, '').length;
    };

    /**
     * The x each COLUMN of signs puts its dot at.
     *
     * <p>The dot used to sit just past its own sign's first line, so its x moved with the length
     * of that text — and two signs in the same column ended up with their dots at different
     * horizontal positions. Gemini and Leo both start at x=20 and should read as one vertical
     * column; they did not, because their planet lists are different lengths.
     *
     * <p>Grouping by the column's shared starting x and taking the WIDEST first line in that
     * column aligns them without ever letting a dot land on top of text. It also stops the dots
     * shuffling sideways during an animation as planets move between signs.
     */
    const dotXByColumn = (() => {
      const widest = {};
      for (let i = 0; i < SIGN_LINE_COORDS.length; i++) {
        const coords = SIGN_LINE_COORDS[i];
        if (!coords || coords.length === 0) continue;
        const baseX = [...coords].sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x))[0].x;
        const len = firstLineLength(i);
        // 120 is the placeholder an empty sign has always used, so a dot does not jump when a
        // house empties mid-animation.
        const width = len > 0 ? len * 9 : 120;
        widest[baseX] = Math.max(widest[baseX] || 0, width);
      }
      return widest;
    })();

    // The dot marks an occupied house, so it has to be inside that house. Geometry in
    // utils/visualUniverseGeometry.js — this is only where it is applied.
    const cellKey = cellKeyFor(cellDividers(width, height));

    const dotPositionForSign = (signIdx) => {
      const coords = SIGN_LINE_COORDS[signIdx];
      if (!coords || coords.length === 0) return null;
      const ordered = [...coords].sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x));
      const { x, y } = ordered[0];
      const len = firstLineLength(signIdx);

      const preferredX = x + (dotXByColumn[x] || 120) + 12;
      const home = homeCellFor(coords, cellKey);
      return { x: containDotX(preferredX, x, y, home, cellKey), y, occupied: len > 0 };
    };

    /**
     * Utility to draw a labeled line between two signs on the SVG chart, supporting multiple parallel lines.
     * @param {string} signA - Name of the first sign (e.g. 'ARIES')
     * @param {string} signB - Name of the second sign (e.g. 'LIBRA')
     * @param {string} label - Text to display near the line
     * @param {string} color - Stroke color for the line
     * @param {number} thickness - Stroke width for the line
     * @param {number} [index=0] - Index of this line among all lines between the same two signs
     * @param {number} [total=1] - Total number of lines between the same two signs
     * @returns SVG group element with line and label
     *
     * To avoid overlap, pass the index and total for each line between the same sign pair.
     */

    // Usage: for multiple lines, call with index and total, e.g.:
    // drawLabeledSignLine('ARIES', 'LIBRA', 'Label1', '#4caf50', 5, 0, 3)
    // drawLabeledSignLine('ARIES', 'LIBRA', 'Label2', '#e91e63', 5, 1, 3)
    // drawLabeledSignLine('ARIES', 'LIBRA', 'Label3', '#2196f3', 5, 2, 3)

    const writeText = (x, y, text, isSignName = false) => (
      <text
        key={`text-${x}-${y}-${text}`}
        x={width * x}
        y={height * y}
        textAnchor="start"
        dominantBaseline="hanging"
        style={{
          fontSize: isSignName ? '72px' : '20px',
          fontWeight: isSignName ? 'bold' : 'normal',
          fill: isSignName ? '#e2f1ff' : '#555555',
          fontFamily: 'Arial, sans-serif'
        }}
      >
        {text}
      </text>
    );
    // --- BEGIN SIGN TEXT LINE RENDERING ---
    // Helper: Parse text with <t>n</t>, <t>s</t>, <t>f</t> for color and <b></b> for bold, supporting nesting
    const parseStyledText = (input) => {
      // Recursive parser for nested tags
      const TAG_REGEX = /<(t|b)(?:>([nsf])|)>|<\/(t|b)>/gi;
      const stack = [{ color: 'black', bold: false }];
      const colorMap = { n: 'black', s: 'red', f: 'blue' };

      let result = [];
      let lastIdx = 0;
      let match;
      let curr = stack[stack.length - 1];
      while ((match = TAG_REGEX.exec(input))) {
        if (match.index > lastIdx) {
          // Push text between tags
          const text = input.slice(lastIdx, match.index);
          if (text) result.push({ text, ...curr });
        }
        if (match[1]) {
          // Opening tag
          if (match[1] === 't') {
            // Color tag
            stack.push({ ...curr, color: colorMap[match[2]] || 'black' });
          } else if (match[1] === 'b') {
            stack.push({ ...curr, bold: true });
          }
          curr = stack[stack.length - 1];
        } else if (match[3]) {
          // Closing tag
          stack.pop();
          curr = stack[stack.length - 1];
        }
        lastIdx = TAG_REGEX.lastIndex;
      }
      if (lastIdx < input.length) {
        result.push({ text: input.slice(lastIdx), ...curr });
      }
      return result;
    };

    // Render sign text lines with color and bold, and tooltip on hover

    const renderSignTextLines = () => {
      if (!signTextLines) return null;
      const elements = [];
      for (let signIdx = 0; signIdx < SIGN_LINE_COORDS.length; ++signIdx) {
        const orderedCoords = [...SIGN_LINE_COORDS[signIdx]].sort((a, b) => {
          if (a.y !== b.y) return a.y - b.y;
          return a.x - b.x;
        });
        // The dot's position comes from dotPositionForSign so the aspect lines and the dot cannot
        // drift apart. Two expressions for one position diverge the moment either is touched, and
        // a line ending a few pixels off its dot reads as a rendering fault.
        const dot = dotPositionForSign(signIdx);
        if (!dot) continue;
        const { x: dotX, y } = dot;
        // Dot with tooltip (larger)
        elements.push(
          <foreignObject
            key={`signdot-${signIdx}`}
            x={dotX - 16}
            y={y - 12}
            width={40}
            height={40}
            style={{ overflow: 'visible' }}
          >
            <Tooltip
              title={<span style={{ whiteSpace: 'pre-line' }}>{signTooltips[signIdx]}</span>}
              placement="top"
              arrow
            >
              <svg width={32} height={32} style={{ pointerEvents: 'all', cursor: 'pointer' }}>
                <circle cx={16} cy={16} r={10} fill="#8e44ad" stroke="#fff" strokeWidth={2} />
              </svg>
            </Tooltip>
          </foreignObject>
        );
        // Render the sign text lines as before
        for (let lineIdx = 0; lineIdx < orderedCoords.length; ++lineIdx) {
          const text = signTextLines[signIdx][lineIdx];
          if (text && typeof text === 'string' && text.trim()) {
            const { x, y } = orderedCoords[lineIdx];
            const styledSegments = parseStyledText(text);
            elements.push(
              <text
                key={`signline-${signIdx}-${lineIdx}`}
                x={x}
                y={y}
                fontSize="16"
                fontFamily="Arial, sans-serif"
              >
                {styledSegments.map((seg, i) => (
                  <tspan
                    key={i}
                    fill={seg.color}
                    fontWeight={seg.bold ? 'bold' : 'bold'}
                    className={seg.color === 'blue' ? 'flash' : undefined}
                  >
                    {seg.text}
                  </tspan>
                ))}
              </text>
            );
          }
        }
      }
      return elements;
    };

    // --- END SIGN TEXT LINE RENDERING ---


    
    const houses = [
      { path: `M ${topCenter.x},${topCenter.y} L ${rightCenter.x},${rightCenter.y} L ${center.x},${center.y} Z`, fill: '#f0f8ff' },
      { path: `M ${topLeft.x},${topLeft.y} L ${topCenter.x},${topCenter.y} L ${center.x},${center.y} Z`, fill: '#e6e6fa' },
      { path: `M ${topLeft.x},${topLeft.y} L ${leftCenter.x},${leftCenter.y} L ${center.x},${center.y} Z`, fill: '#e0ffff' },
      { path: `M ${leftCenter.x},${leftCenter.y} L ${bottomLeft.x},${bottomLeft.y} L ${center.x},${center.y} Z`, fill: '#f0fff0' },
      { path: `M ${bottomLeft.x},${bottomLeft.y} L ${bottomCenter.x},${bottomCenter.y} L ${center.x},${center.y} Z`, fill: '#fff0f5' },
      { path: `M ${bottomCenter.x},${bottomCenter.y} L ${bottomRight.x},${bottomRight.y} L ${center.x},${center.y} Z`, fill: '#f5fffa' },
      { path: `M ${bottomRight.x},${bottomRight.y} L ${rightCenter.x},${rightCenter.y} L ${center.x},${center.y} Z`, fill: '#f0f0ff' },
      { path: `M ${rightCenter.x},${rightCenter.y} L ${topRight.x},${topRight.y} L ${center.x},${center.y} Z`, fill: '#fffaf0' },
      { path: `M ${topRight.x},${topRight.y} L ${topCenter.x},${topCenter.y} L ${center.x},${center.y} Z`, fill: '#f5f5f5' },
      { path: `M ${topCenter.x},${topCenter.y} L ${rightCenter.x},${rightCenter.y} L ${bottomCenter.x},${bottomCenter.y} L ${leftCenter.x},${leftCenter.y} Z`, fill: '#f0ffff' },
      { path: `M ${rightCenter.x},${rightCenter.y} L ${bottomRight.x},${bottomRight.y} L ${center.x},${center.y} Z`, fill: '#fff5ee' },
      { path: `M ${topRight.x},${topRight.y} L ${rightCenter.x},${rightCenter.y} L ${center.x},${center.y} Z`, fill: '#f0ffe0' },
    ];
    return (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', height: '100%' }}
        // --- BEGIN TEMPORARY COORDINATE CAPTURE ---
        // onClick={e => {
        //   if (typeof onChartClick === 'function' && e.button === 0) {
        //     // Only handle left mouse button
        //     const svg = e.currentTarget;
        //     const rect = svg.getBoundingClientRect();
        //     // Calculate SVG-relative coordinates
        //     const x = Math.round((e.clientX - rect.left) * (width / rect.width));
        //     const y = Math.round((e.clientY - rect.top) * (height / rect.height));
        //     // Only accept clicks inside SVG bounds
        //     if (x >= 0 && x <= width && y >= 0 && y <= height) {
        //       onChartClick({ x, y });
        //     }
        //   }
        // }}
        // --- END TEMPORARY COORDINATE CAPTURE ---
      >
        {showBackground && houses.map((house, index) => (
          <path 
            key={`house-${index}`} 
            d={house.path} 
            fill={house.fill} 
            stroke="none"
          />
        ))}
        <rect x="0" y="0" width={width} height={height} fill="none" stroke="black" strokeWidth="2" />
        <line x1="0" y1="0" x2="0" y2={height} stroke="black" strokeWidth="4" />
        <line x1={width} y1="0" x2={width} y2={height} stroke="black" strokeWidth="4" />
        <line x1={topLeft.x} y1={topLeft.y} x2={bottomRight.x} y2={bottomRight.y} stroke="black" strokeWidth="2" />
        <line x1={topRight.x} y1={topRight.y} x2={bottomLeft.x} y2={bottomLeft.y} stroke="black" strokeWidth="2" />
        <line x1={topCenter.x} y1={topCenter.y} x2={rightCenter.x} y2={rightCenter.y} stroke="black" strokeWidth="2" />
        <line x1={rightCenter.x} y1={rightCenter.y} x2={bottomCenter.x} y2={bottomCenter.y} stroke="black" strokeWidth="2" />
        <line x1={bottomCenter.x} y1={bottomCenter.y} x2={leftCenter.x} y2={leftCenter.y} stroke="black" strokeWidth="2" />
        <line x1={leftCenter.x} y1={leftCenter.y} x2={topCenter.x} y2={topCenter.y} stroke="black" strokeWidth="2" />
        <g>
          {signPositions.map((item) => (
        <g key={item.sign}>
          {writeText(item.x / width, item.y / height, item.sign, true)}
          {/* No sample lines rendered; backend integration will populate these dynamically */}
        </g>
      ))}
        </g>
      {/* --- BEGIN SIGN TEXT LINES --- */}
      {renderSignTextLines()}
      {/* --- BEGIN ASPECT LINES ---
          This is a South-Indian square chart, not a circular wheel, so an aspect is drawn between
          the two bodies' SIGN CELLS rather than between points on a rim. Bodies in the same sign
          therefore have nowhere to draw a line to; those are labelled at the cell instead.
          Rendered last so the lines sit above the sign text rather than under it. */}
      {aspects.length > 0 && (
        <g className="aspect-lines">
          {(() => {
            // Resolve each aspect to the two magenta dots it connects, dropping any whose house
            // is empty. A house with no planet has nothing at the end of the line to be about,
            // and the anchor arithmetic for an empty sign is a placeholder rather than a position.
            const drawable = [];
            aspects.forEach((asp) => {
              const aSign = Math.floor(asp.aLongitude / 30) % 12;
              const bSign = Math.floor(asp.bLongitude / 30) % 12;
              const from = dotPositionForSign(aSign);
              const to = dotPositionForSign(bSign);
              if (!from || !to || !from.occupied || !to.occupied) return;
              drawable.push({ asp, from, to, aSign, bSign });
            });

            // How many lines already touch each house, so overlapping paths can be told apart.
            // Several aspects routinely share an endpoint - a body in a busy sign aspects three or
            // four others - and drawn identically they resolve into one thick smear that says
            // nothing about how many relationships it represents.
            const touchCount = {};
            drawable.forEach(({ aSign, bSign }) => {
              touchCount[aSign] = (touchCount[aSign] || 0) + 1;
              touchCount[bSign] = (touchCount[bSign] || 0) + 1;
            });

            // Dash patterns, in order of use. All dotted, all thin; they differ in rhythm so a
            // reader can follow one line through a crossing without needing to distinguish colour.
            const DASH_PATTERNS = ['2 5', '2 10', '6 5', '1 4', '8 4 2 4', '4 8'];

            // A trine draws SOLID, every other aspect dotted. It is the one aspect here read as
            // supportive rather than tense, and a solid line separates it at a glance without
            // needing the label. 240 is not a separate case: detection folds to [0,180], so a
            // trine either way round is already reported as 120.
            const isSolid = (angle) => angle === 120;

            const usedPerPair = {};
            return drawable.map(({ asp, from, to, aSign, bSign }, i) => {
              const colour = ASPECT_COLORS[asp.angle] || '#6a1b9a';
              const label = aspectLabel(asp);
              const title = `${asp.a} - ${asp.b}: ${asp.name}, orb ${asp.orbLabel}`;
              const key = `aspect-${asp.a}-${asp.b}-${i}`;

              // Both bodies in the same house: no line to draw, so the aspect is stated at the dot.
              if (aSign === bSign) {
                // Both bodies in one house is the commonest shape of a conjunction, and it has
                // no line at all - only this label. It is the case the pulse matters most for.
                return (
                  <text key={key} x={from.x} y={from.y + 30} fontSize="18" fill={colour}
                        className={asp.angle === 0 ? 'aspect-conjunction-text' : undefined}
                        textAnchor="middle" fontWeight="bold"
                        style={{ paintOrder: 'stroke', stroke: '#fff', strokeWidth: 4 }}>
                    <title>{title}</title>
                    {label}
                  </text>
                );
              }

              // A distinct rhythm per line, and a small perpendicular offset for the second and
              // later lines sharing a pair of houses, so two aspects between the same two cells
              // do not lie exactly on top of one another.
              const pairKey = [aSign, bSign].sort().join('-');
              const nth = usedPerPair[pairKey] = (usedPerPair[pairKey] || 0) + 1;
              const busiest = Math.max(touchCount[aSign] || 1, touchCount[bSign] || 1);
              const dash = DASH_PATTERNS[(i + nth) % DASH_PATTERNS.length];

              const dx = to.x - from.x;
              const dy = to.y - from.y;
              const len = Math.hypot(dx, dy) || 1;
              // Perpendicular unit vector, scaled by how crowded the endpoints are: lines fan out
              // only where they would otherwise collide.
              const spread = busiest > 1 ? ((nth - 1) - (busiest - 1) / 2) * 9 : 0;
              const offX = (-dy / len) * spread;
              const offY = (dx / len) * spread;

              const x1 = from.x + offX;
              const y1 = from.y + offY;
              const x2 = to.x + offX;
              const y2 = to.y + offY;
              const midX = (x1 + x2) / 2;
              const midY = (y1 + y2) / 2;

              // Rotated onto the line so the label reads along it, flipped back where that would
              // leave it upside down - a chart is read one way up.
              let angleDeg = Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI;
              if (angleDeg > 90 || angleDeg < -90) angleDeg += 180;

              // A dozen dotted lines crossing one square chart is unreadable however they are
              // dashed, so hovering ONE brings it forward and pushes the rest back. That is what
              // makes a crowded chart legible: not making every line clearer, but making one
              // line clearly the subject.
              const isHovered = hoveredAspect === key;
              const dimmed = hoveredAspect !== null && !isHovered;
              const hover = () => onHoverAspect && onHoverAspect(key);
              const unhover = () => onHoverAspect && onHoverAspect(null);

              return (
                <g key={key} onMouseEnter={hover} onMouseLeave={unhover}
                   style={{ cursor: 'pointer' }}>
                  <title>{title}</title>
                  {/* An invisible fat line under the thin one: a 1.5px dotted path is almost
                      impossible to hit with a cursor, and a hover target you cannot hit is the
                      same as no hover target. */}
                  <line x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke="transparent" strokeWidth="18" pointerEvents="stroke" />
                  <line x1={x1} y1={y1} x2={x2} y2={y2}
                        className={asp.angle === 0 && !dimmed ? 'aspect-conjunction' : undefined}
                        stroke={colour} strokeWidth={isHovered ? 4 : (isSolid(asp.angle) ? 2 : 1.5)}
                        strokeDasharray={isSolid(asp.angle) ? undefined : dash}
                        strokeLinecap="round"
                        opacity={dimmed ? 0.1 : 0.95} pointerEvents="none" />
                  {/* The label is a hover target in its own right. It had pointerEvents="none",
                      so moving the cursor onto the TEXT - the obvious thing to aim at, and far
                      easier to hit than a 1.5px line - did nothing at all. That is why the
                      highlight appeared not to work. */}
                  <text x={midX} y={midY} fontSize={isHovered ? 24 : 18}
                        className={asp.angle === 0 && !dimmed ? 'aspect-conjunction-text' : undefined}
                        fill={isHovered ? '#000' : colour}
                        textAnchor="middle" fontWeight="bold"
                        transform={`rotate(${angleDeg.toFixed(2)} ${midX} ${midY})`}
                        dy="-6"
                        opacity={dimmed ? 0.1 : 1}
                        style={{
                          paintOrder: 'stroke',
                          stroke: isHovered ? colour : '#fff',
                          strokeWidth: isHovered ? 7 : 4,
                          cursor: 'pointer',
                        }}>
                    {label}
                  </text>
                </g>
              );
            });
          })()}
        </g>
      )}
      </svg>
    );
};

ChartSVG.propTypes = {
  showBackground: PropTypes.bool.isRequired,
  aspects: PropTypes.array,
  hoveredAspect: PropTypes.string,
  onHoverAspect: PropTypes.func,
  horoscopeData: PropTypes.array,
  signTextLines: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
  signTooltips: PropTypes.arrayOf(PropTypes.string),
};

import { useAuth } from '../context/AuthContext';

const VisualUniverseDialog = ({ open, onClose, initialDate }) => {
  const { api } = useAuth();
  // --- BEGIN TEMPORARY COORDINATE CAPTURE ---
// Handler for SVG click events
// const handleChartClick = ({ x, y }) => {
//   setStatusText(`Clicked at (${x}, ${y})`);
// };
// --- END TEMPORARY COORDINATE CAPTURE ---
  const [statusText, setStatusText] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [showBackground, setShowBackground] = useState(true);

  // Frame controls. Two independent choices - where the observer stands, and where the zodiac
  // starts - so they are two groups rather than one list of four, matching how ZodiacFrame models
  // it on the backend. Geo-sidereal is the default, matching every other study in the app and
  // what this wheel drew before it had a frame control at all.
  const [centre, setCentre] = useState('geo');       // 'geo' | 'helio'
  const [zodiac, setZodiac] = useState('sidereal');  // 'sidereal' | 'tropical'
  const [showAspects, setShowAspects] = useState(false);
  // Hidden by default: it is a second reading of what the chart already shows, useful when the
  // lines are crowded and in the way when they are not.
  const [showSummary, setShowSummary] = useState(false);
  /**
   * The orb the reader is willing to call an aspect, in whole degrees.
   *
   * <p>Held as a STRING so the field can be empty mid-edit. A number state coerces "" to 0, which
   * snaps the caret to a value the user did not type and makes the box impossible to clear before
   * retyping. What is typed and what is APPLIED are kept separate: `orb` below is the clamped
   * reading, and it is the only one detection ever sees.
   */
  const [orbInput, setOrbInput] = useState(String(MAX_ORB));
  const orb = clampOrb(orbInput);
  /**
   * Whether the reader has typed an orb of their own.
   *
   * <p>Each set carries its own default — 7 for Gann, 5 otherwise — and switching sets should move
   * the orb to that default. But only while it IS a default: silently discarding a value the reader
   * typed, because they toggled something else, is the kind of thing that makes a control feel
   * broken. Once touched, the number is theirs and the set stops overwriting it.
   */
  const [orbEdited, setOrbEdited] = useState(false);
  /**
   * Which named angle set is in force.
   *
   * <p>Both sets now use the same four angles. What separates them is the orb and the requirement
   * that an aspect be part of a configuration — see anglesForSet and configurationAspects.
   */
  const [aspectSet, setAspectSet] = useState(ASPECT_SETS.ALL);

  /** Move the orb to the incoming set's default, unless the reader has set one deliberately. */
  const chooseAspectSet = (next) => {
    setAspectSet(next);
    if (!orbEdited) setOrbInput(String(defaultOrbForSet(next)));
  };

  // Positions for the instant on screen, and the aspects derived from them. Held separately
  // because the positions are what the backend returns and the aspects are a pure function of
  // them - recomputing aspects on a slider change must not need another round trip.
  const [positions, setPositions] = useState([]);

  // Which aspect line the cursor is on. Only the key is kept: the highlight is the whole
  // mechanism now, so nothing needs the aspect object itself. A floater restating the label was
  // tried and removed - it answered a question the highlight already answers, and put a panel
  // over the chart to do it.
  const [hoveredAspect, setHoveredAspect] = useState(null);
  const handleHoverAspect = React.useCallback((key) => setHoveredAspect(key), []);
  const [rate, setRate] = useState(1);
  // Default change unit: 24 hours (instead of 2)
  const [unit, setUnit] = useState(24); // CHANGED: default is now 24 hours
  // Load dates from localStorage or use defaults
  const [fromDate, setFromDate] = useState(null);

  useEffect(() => {
    if (initialDate) {
      setFromDate(dayjs(initialDate));
    } else {
      const saved = localStorage.getItem('visualUniverse_fromDate');
      setFromDate(saved ? dayjs(saved) : dayjs());
    }
  }, [initialDate, open]);

  const [toDate, setToDate] = useState(() => {
    const saved = localStorage.getItem('visualUniverse_toDate');
    return saved ? dayjs(saved) : dayjs('2026-12-31');
  });
  // Previous:
  // const [fromDate, setFromDate] = useState('2025-01-01');
  // const [toDate, setToDate] = useState('2026-12-31');
  // Animation phase: 'idle' | 'running' | 'paused'
  const [animationPhase, setAnimationPhase] = useState('idle');
  const [signTextLines, setSignTextLines] = useState(() => Array(12).fill([]));
  const [signTooltips, setSignTooltips] = useState(() => Array(12).fill(''));

  // Track the chart date currently being displayed
  const [currentChartDate, setCurrentChartDate] = useState(null);
  // Animation direction: 1 = forward, -1 = reverse
  const [animationDirection, setAnimationDirection] = useState(1);


  const timerRef = React.useRef(null);
  const lastServiceTimeRef = React.useRef(null);

  const isStartEnabled = fromDate && toDate;

  // Auto-set direction to forward when start is enabled
  React.useEffect(() => {
    if (isStartEnabled) setAnimationDirection(1);
  }, [isStartEnabled]);

  // Keep currentChartDate in sync with fromDate and animation
  React.useEffect(() => {
    if (animationPhase === 'idle') {
      setCurrentChartDate(fromDate);
    }
  }, [fromDate, animationPhase]);


  // Declared ABOVE fetchPositions, which calls it. It works either way today, because a callback
  // body runs long after every const in the component has initialised — but naming it in that
  // callback's dependency array would be a temporal dead zone reference that throws on mount, and
  // App.tdz.test.js only guards App.js. Ordering it correctly removes the trap rather than
  // relying on nobody stepping in it.
  const formatDateTime = (dateObj) => {
    if (!dateObj) return '';
    const d = dayjs(dateObj);
    return d.format('YYYY-MM-DDTHH:mm:ss');
  };

  // fetchHoroscope is declared further down and is a new function every render, so the frame
  // effect below reaches it through a ref. Naming the function itself in that effect would be
  // a temporal dead zone reference — the crash App.tdz.test.js exists for, in a file that
  // guard does not cover.
  const fetchHoroscopeRef = React.useRef(null);

  /** The backend's ZodiacFrame alias for the two radio groups. */
  const frameName = React.useMemo(() => {
    if (centre === 'helio') return zodiac === 'sidereal' ? 'helio-sidereal' : 'helio';
    return zodiac === 'sidereal' ? 'geo-sidereal' : 'geo-tropical';
  }, [centre, zodiac]);

  /**
   * Aspects for the instant on screen.
   *
   * Derived rather than stored: the angle set and orb are fixed, so the aspects are a pure
   * function of the positions, and deriving them means they cannot drift out of step with the
   * wheel. Empty when the checkbox is off, so nothing is computed for a hidden layer.
   */
  const aspects = React.useMemo(
    () => {
      if (!showAspects) return [];
      const found = findAspects(positions, anglesForSet(aspectSet), orb);
      // Gann asks about geometry, and a lone pair is not geometry. The six-angle set finds roughly
      // twice as many aspects as the standard four, so without this the shapes it exists to reveal
      // are buried under the isolated pairs it also picks up.
      return aspectSet === ASPECT_SETS.GANN ? configurationAspects(found) : found;
    },
    [showAspects, positions, aspectSet, orb],
  );

  /**
   * Positions for one instant, in the chosen frame.
   *
   * Only fetched while Show Aspects is on. The wheel itself is drawn from /api/horoscope, which is
   * unchanged; this is an additional read that exists solely to feed the aspect layer, and running
   * it during an animation nobody is watching aspects on would double the request rate for
   * nothing.
   */
  const fetchPositions = React.useCallback(async (dateObj) => {
    if (!dateObj) return;
    try {
      const res = await api.post('/api/followstudy/positions', {
        date: formatDateTime(dateObj),
        zodiac: frameName,
        // The wheel plots the TRUE node for Rahu and derives Ketu from it. PlanetIds resolves
        // Rahu to the MEAN node by default, and the two differ by up to 1.7 degrees — enough to
        // put a body in a different sign near a cusp, and the aspect line on the wrong house.
        trueNode: true,
      });
      setPositions(Array.isArray(res.data?.positions) ? res.data.positions : []);
    } catch (e) {
      // Best-effort: the wheel is still correct without the aspect layer, so a failure here must
      // not blank the chart. Cleared rather than left stale - aspects from a previous instant
      // drawn over the current one would be worse than none.
      setPositions([]);
      setStatusText('Could not load positions for aspects');
    }
  }, [api, frameName]);

  // Refetch when the instant changes, or when the frame does. Guarded on showAspects so turning
  // the layer off stops the traffic rather than merely hiding the result.
  React.useEffect(() => {
    if (!showAspects) {
      setPositions([]);
      return;
    }
    fetchPositions(currentChartDate);
  }, [showAspects, currentChartDate, fetchPositions]);

  // Changing the frame has to redraw the WHEEL as well. Without this the radios moved the aspect
  // layer immediately and the planet text only caught up on the next animation tick — so a paused
  // chart showed lines computed in one frame over planets drawn in another.
  React.useEffect(() => {
    if (currentChartDate) fetchHoroscopeRef.current(formatDateTime(currentChartDate));
    // currentChartDate is deliberately absent: the timer already refetches on every tick, and
    // including it here would double every request during an animation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameName]);

  /**
   * THE DIALOG OPENS ON THE FROM-DATE CHART, AND STAYS THERE UNTIL START IS PRESSED.
   *
   * <p>It opened BLANK. `currentChartDate` was synced to `fromDate` on mount, and the aspect
   * layer followed it — but the WHEEL is only ever drawn by `fetchHoroscope`, and the only three
   * callers were the frame radios, the animation timer and the Start button. The frame effect
   * does run on mount and is skipped, because `currentChartDate` is still null on the first
   * render and is only filled in by the sync effect afterwards. So nothing drew the wheel.
   *
   * <p>Guarded on IDLE, which is what keeps it from fighting the timer: during a run the date
   * changes on every tick and the timer is already fetching for it, so a second effect watching
   * the same value would double every request. Pause leaves the phase at `paused`, so the frame
   * a reader stopped on stays put.
   *
   * <p>⚠ It also fires when a run FINISHES, and that is a fix rather than a side effect. The
   * sync effect above already pulls `currentChartDate` back to `fromDate` at the end, so the
   * aspect layer jumped to the start while the wheel stayed on the last frame — two layers of
   * one chart showing different instants. Now both show the start.
   */
  React.useEffect(() => {
    if (!open || animationPhase !== 'idle' || !currentChartDate) return;
    fetchHoroscopeRef.current(formatDateTime(currentChartDate));
    // Through the REF, so this calls the current render's closure rather than the first one —
    // the same reason the frame effect above uses it. Listing `fetchHoroscope` itself would
    // re-run this on every render, because it is a plain function declared in the body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, animationPhase, currentChartDate]);

  // Helper: convert Dayjs or Date to epoch milliseconds
  const toEpochMillis = (dateObj) => {
    if (!dateObj) return null;
    if (typeof dateObj.valueOf === 'function') return dateObj.valueOf();
    return new Date(dateObj).getTime();
  };

  // Real backend call for horoscope (now sending currentDate and previousDate)
  const fetchHoroscope = async (currentDate) => {
    try {
      setStatusText('Loading horoscope...');
      // The frame goes with it, so the WHEEL follows the radios too. Without this the wheel
      // stayed geocentric sidereal while the aspects honoured the selection, and five of
      // seven bodies sat in a different sign — every line drawn to the wrong house.
      const res = await api.post('/api/horoscope', { currentDate, zodiac: frameName });
      const data = res.data;
      // Expecting: data = { lines: [...], tooltips: [...] }
      if (data && Array.isArray(data.lines) && Array.isArray(data.tooltips) && data.lines.length === 12 && data.tooltips.length === 12) {
        setStatusText('');
        setSignTextLines(data.lines);
        setSignTooltips(data.tooltips);
      } else {
        setStatusText('Unexpected data from backend');
        setSignTextLines(Array(12).fill([]));
        setSignTooltips(Array(12).fill(''));
      }
    } catch (e) {
      setStatusText('Error fetching horoscope');
      setSignTextLines(Array(12).fill([]));
      setSignTooltips(Array(12).fill(''));
    }
  };

  // Kept current so the frame effect above calls this render's closure, not the first one.
  fetchHoroscopeRef.current = fetchHoroscope;





  // Timer logic
  React.useEffect(() => {
    if (animationPhase === 'running' && fromDate && toDate) {
      setCurrentChartDate(fromDate);
      const end = new Date(toDate);
      timerRef.current = setInterval(() => {
        const incrementMs = unit * 3600000;
        let nextTime = (lastServiceTimeRef.current ?? toEpochMillis(fromDate)) + animationDirection * incrementMs;
        if ((animationDirection === 1 && nextTime > end.getTime()) || (animationDirection === -1 && nextTime < new Date(fromDate).getTime())) {
          clearInterval(timerRef.current);
          setAnimationPhase('idle');
          setStatusText('Done');
          return;
        }
        // Compute previous time (previous bar)
        const prevTime = nextTime - animationDirection * incrementMs;
        fetchHoroscope(formatDateTime(new Date(nextTime)), formatDateTime(new Date(prevTime))).then(() => {
          lastServiceTimeRef.current = nextTime;
          setCurrentChartDate(new Date(nextTime));
        });
      }, rate * 1000);
      return () => clearInterval(timerRef.current);
    } else {
      clearInterval(timerRef.current);
    }
    // Deliberately omits fetchHoroscope and animationDirection. fetchHoroscope is a plain async
    // function declared in the component body, so it is a new reference on every render — listing
    // it would clear and recreate this interval each time, and an animation that restarts on every
    // render never advances. animationDirection is read when a tick fires, so a mid-run reversal
    // is already picked up without tearing the timer down and losing its phase.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationPhase, fromDate, toDate, rate, unit]);

  // START button handler
  const handleStart = () => {
    setAnimationDirection(1); // ALWAYS start in forward direction
    setAnimationPhase('running');
    const startMillis = toEpochMillis(fromDate);
    lastServiceTimeRef.current = startMillis;
    setCurrentChartDate(fromDate);
    // For first call, previousDate = startMillis - unit * 3600000
    const prevMillis = startMillis - unit * 3600000;
    fetchHoroscope(formatDateTime(new Date(startMillis)), formatDateTime(new Date(prevMillis)));
  };


  // PAUSE/CONTINUE handler
  const handlePauseContinue = () => {
    if (animationPhase === 'running') {
      setAnimationPhase('paused');
    } else if (animationPhase === 'paused') {
      setAnimationPhase('running');
    }
  };




  // Cleanup: stop timer and reset state on CLOSE
  const handleClose = () => {
    clearInterval(timerRef.current);
    setAnimationPhase('idle');
    setStatusText('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          height: '95vh',
          width: 'calc((95vh - 175px) * 2916 / 1026)',
          maxWidth: '95vw',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 2 }}>
        <DialogTitle sx={{ m: 0, p: 0, fontSize: 28, lineHeight: 1.2, flex: 'none', minWidth: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
          <span>Visual Universe</span>
          <HelpIcon helpKey="visualUniverseDialog" />
        </DialogTitle>
        {/*
          Four groups, each in its own outlined box with a caption.
          Previously these were eight controls in one undifferentiated row, and the two radio
          groups read as a single list of four mutually exclusive options rather than as two
          independent choices — which is exactly the wrong mental model, since Geo/Helio and
          Sidereal/Tropical combine into four frames rather than competing.
          The caption above each group is what states the question the controls answer; the border
          is what shows where one question ends and the next begins.
        */}
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2 }}>
          <HeaderGroup label="Display">
            <FormControlLabel
              control={<Checkbox size="small" checked={showConfig} onChange={() => setShowConfig(!showConfig)} disabled />}
              label="Show Config"
              sx={{ mr: 1 }}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={showBackground} onChange={() => setShowBackground(!showBackground)} />}
              label="Show Background"
              sx={{ mr: 0 }}
            />
          </HeaderGroup>

          <HeaderGroup label="Centre">
            <RadioGroup row value={centre} onChange={(e) => setCentre(e.target.value)}>
              <FormControlLabel value="geo" control={<Radio size="small" />} label="Geo" />
              <FormControlLabel value="helio" control={<Radio size="small" />} label="Helio" sx={{ mr: 0 }} />
            </RadioGroup>
          </HeaderGroup>

          <HeaderGroup label="Zodiac">
            <RadioGroup row value={zodiac} onChange={(e) => setZodiac(e.target.value)}>
              <FormControlLabel value="sidereal" control={<Radio size="small" />} label="Sidereal" />
              <FormControlLabel value="tropical" control={<Radio size="small" />} label="Tropical" sx={{ mr: 0 }} />
            </RadioGroup>
          </HeaderGroup>

          {/* The frame the two groups above combine into, named the way the backend names it, so
              what is on screen and what was requested can be compared without guessing. */}
          <HeaderGroup label={`Aspects · ${frameName}`}>
            <FormControlLabel
              control={<Checkbox size="small" checked={showAspects} onChange={() => setShowAspects(!showAspects)} />}
              label={showAspects && aspects.length > 0 ? `Show Aspects (${aspects.length})` : 'Show Aspects'}
              sx={{ mr: 1 }}
            />
            {/* The tolerance, in whole degrees. Typed rather than picked from a list: the useful
                range is ten values, and a reader tightening an orb almost always wants the next
                one down, which the spinner gives in a single keystroke. */}
            {/*
              Not gated on showAspects. These three describe HOW aspects are found, so setting one
              before turning aspects on is the natural order, and MUI renders a disabled contained
              button as grey text with no background at all - against this header it reads as a
              control that has been removed rather than one that is waiting. Reported as exactly
              that: "the edit box for Orb AND Gann button disappeared".
            */}
            <TextField
              label="Orb"
              type="number"
              size="small"
              value={orbInput}
              onChange={(e) => { setOrbInput(e.target.value); setOrbEdited(true); }}
              // Clamped on BLUR, not on change. Correcting mid-keystroke makes "10" impossible to
              // type: the "1" is accepted, and the "0" then lands on an already-clamped value.
              onBlur={() => setOrbInput(String(clampOrb(orbInput)))}
              inputProps={{ min: ORB_MIN, max: ORB_MAX, step: 1, 'aria-label': 'Aspect orb in degrees' }}
              sx={{ width: 86, mr: 1 }}
            />
            {/* Named for the set in force, like the direction toggle: a button reading "Gann"
                cannot say whether Gann is on or is what a click would turn on. */}
            <Button
              size="small"
              variant="contained"
              onClick={() => chooseAspectSet(aspectSet === ASPECT_SETS.GANN ? ASPECT_SETS.ALL : ASPECT_SETS.GANN)}
              sx={{
                minWidth: 72, mr: 1, textTransform: 'none', fontWeight: 700,
                backgroundColor: aspectSet === ASPECT_SETS.GANN ? '#ef6c00' : '#455a64',
                '&:hover': { backgroundColor: aspectSet === ASPECT_SETS.GANN ? '#e65100' : '#37474f' },
              }}
            >
              {aspectSet === ASPECT_SETS.GANN ? 'Gann' : 'All'}
            </Button>
            {/* Named for what a click DOES, not for the state it is in: a button reading
                "Summary" while the summary is open is ambiguous about which it means. */}
            <Button
              size="small"
              variant={showSummary ? 'contained' : 'outlined'}
              onClick={() => setShowSummary(v => !v)}
              sx={{ minWidth: 118, textTransform: 'none' }}
            >
              {showSummary ? 'No Summary' : 'Summary'}
            </Button>
          </HeaderGroup>
        </Box>
      </Box>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden', height: '100%', position: 'relative' }}>
        {/* ConfigBox: empty container for future animation controls */}
        {showConfig && (
          <Box
            sx={{
              width: '100%',
              height: '50%',
              minHeight: 200,
              maxHeight: '50%',
              border: '2px dashed #8e44ad',
              // A white-lilac panel on a dark dialog. It holds nothing today, so there is no text
              // to be unreadable -- but the rectangle itself was the wrong colour, and a future
              // control dropped into it would have inherited the same problem.
              background: (theme) => (theme.palette.mode === 'dark' ? 'rgba(142, 68, 173, 0.10)' : '#faf7ff'),
              borderRadius: 3,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              color: '#8e44ad',
              fontSize: 20,
            }}
          >
            {/* ConfigBox (empty) - future controls go here */}
          </Box>
        )}
        {/*
          The playback direction and the instant on screen, on one row directly above the chart.
          Both were absolutely positioned OVER the chart before - the buttons at top:16 and the
          date at top:48 - which put them on top of the top houses and left the two reading as
          unrelated controls. In normal flow they sit above the chart's top border instead, and
          the direction sits beside the date it applies to.
        */}
        {!showConfig && (
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
            // pt, not just pb: an outlined TextField's floating label sits ~9px ABOVE the input's
            // own box, and the dialog content scrolls, so with no top padding the label is cut in
            // half by the container edge. Measured at 9px overhang; 10 clears it.
            px: 2, pt: 1.25, pb: 1, width: '100%', flex: 'none',
          }}>
            {/* One button, not two. Two buttons both styled as active said nothing about which
                direction was running; this one names the state it is IN and toggles on click. */}
            <Button
              variant="contained"
              size="small"
              onClick={() => setAnimationDirection(d => (d === 1 ? -1 : 1))}
              startIcon={<span style={{ fontSize: 16, lineHeight: 1 }}>{animationDirection === 1 ? '\u25B6' : '\u25C0'}</span>}
              sx={{
                minWidth: 118, fontWeight: 'bold', textTransform: 'none',
                backgroundColor: animationDirection === 1 ? '#27ae60' : '#8e44ad',
                '&:hover': { backgroundColor: animationDirection === 1 ? '#219150' : '#7d3c98' },
              }}
            >
              {animationDirection === 1 ? 'Forward' : 'Reverse'}
            </Button>
            <TextField
              label="Current Chart Date/Time"
              value={currentChartDate ? (typeof currentChartDate.format === 'function' ? currentChartDate.format('YYYY-MM-DD HH:mm') : String(currentChartDate)) : ''}
              variant="outlined"
              size="small"
              InputProps={{ readOnly: true }}
              /*
               * ⚠ WAS `background: '#f8f8f8'`, which is the whole bug. A near-white fill with no
               * text colour of its own: the value inherited the theme's, and in dark mode that is
               * #e6edf3 on #f8f8f8 -- 1.11:1, which is not faint, it is blank. The label was
               * unreadable for the mirror reason: nothing set it, and MUI's default label colour
               * is tuned for the opposite canvas.
               */
              sx={[readOnlyFieldSx, { minWidth: 260 }]}
            />
          </Box>
        )}
        {/* Chart Area */}
        <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', position: 'relative' }}>
          {/*
            The active aspects, listed.
            Inside the chart container rather than beside it, so it does not narrow the wheel -
            the chart is sized from the dialog's own aspect ratio and taking width from it would
            shrink every house. Scrolls at a fixed height because the count varies from none to
            a dozen and a panel that resizes the layout as an animation runs is worse than one
            that scrolls.
          */}
          {showAspects && showSummary && (
            <Box sx={(theme) => ({
              position: 'absolute', top: 8, right: 8, zIndex: 15,
              width: 190, maxHeight: 'calc(100% - 16px)',
              display: 'flex', flexDirection: 'column',
              borderRadius: 1.5, boxShadow: 2, overflow: 'hidden',
              // Was a 96%-white card, so on a dark canvas it was light text on white. Behind a
              // toggle, which is the likely reason it was not in the report alongside the fields.
              ...floatingPanelSx(theme),
            })}>
              <Typography variant="caption" sx={(theme) => ({
                fontWeight: 700, px: 1, py: 0.5, flex: 'none',
                // Black alphas on a dark panel are nothing at all. The hairline is the panel's
                // own divider colour and the header tint lifts with WHITE in dark mode, so the
                // header stays the lighter strip it is meant to be in both themes.
                borderBottom: `1px solid ${panelDividerColor(theme)}`,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
              })}>
                {/* "Gann · linked" rather than just "Gann": the set is filtered to configurations,
                    so a short list means isolated pairs were dropped, not that the sky is quiet. */}
                Aspects ({aspects.length}) · {aspectSet === ASPECT_SETS.GANN ? 'Gann · linked' : 'All'} · orb {orb}&deg;
              </Typography>
              <Box sx={{ overflowY: 'auto', px: 1, py: 0.5 }}>
                {aspects.length === 0 && (
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>None within orb.</Typography>
                )}
                {/*
                  A grid, not a run of text.

                  Four fixed tracks put the angles under the angles and the orbs under the orbs
                  whatever each row's own values are — which a single string cannot do, since a
                  two-digit angle and a three-digit one shift everything after them. The parts are
                  space-padded as well as gridded, so the reading survives being copied out of the
                  page into somewhere with no grid at all.
                */}
                {aspects.map((asp) => {
                  const key = `${asp.a}-${asp.b}-${asp.angle}`;
                  const isHovered = hoveredAspect !== null && hoveredAspect.includes(`${asp.a}-${asp.b}`);
                  const parts = aspectLabelParts(asp);
                  return (
                    <Box
                      key={key}
                      title={`${asp.a} ${asp.name} ${asp.b} (orb ${asp.orbLabel})`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '2.4ch 4.6ch 5.2ch 2.4ch',
                        columnGap: 0.75,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                        fontSize: 13, lineHeight: 1.7,
                        color: ASPECT_COLORS[asp.angle] || '#6a1b9a',
                        fontWeight: isHovered ? 700 : 500,
                        // Lifts rather than darkens in dark mode -- a black hover on a dark row
                        // reads as the row going missing under the cursor.
                        bgcolor: isHovered
                          ? (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')
                          : 'transparent',
                        borderRadius: 0.5, px: 0.5,
                        whiteSpace: 'pre',
                      }}
                    >
                      <span>{parts.p1}</span>
                      <span style={{ textAlign: 'right' }}>{parts.angle}</span>
                      <span style={{ textAlign: 'right' }}>{parts.orb}</span>
                      <span>{parts.p2}</span>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
          <ChartSVG 
              showBackground={showBackground}
              signTextLines={signTextLines}
              signTooltips={signTooltips}
              aspects={aspects}
              hoveredAspect={hoveredAspect}
              onHoverAspect={handleHoverAspect}
            />
        </Box>
        {/* Bottom Button Row - Two-column layout */}
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', borderTop: '1px solid rgba(0,0,0,0.12)', p: 2, width: '100%' }}>
          {/* Left: CLOSE button */}
          <Box sx={{ flex: 'none', display: 'flex', justifyContent: 'flex-start' }}>
            <Button onClick={handleClose} sx={{ backgroundColor: 'grey.500', '&:hover': { backgroundColor: 'grey.700' }, color: 'white', minWidth: 80 }}>
              CLOSE
            </Button>
          </Box>
          {/* Center: Status message */}
          <TextField
            label="Status Message"
            value={statusText}
            variant="outlined"
            size="small"
            InputProps={{ readOnly: true }}
            // The identical construct in the footer, with the identical defect. Not separately
            // reported, and fixing one of the two would have left the dialog visibly half
            // converted -- they sit a few hundred pixels apart.
            sx={[readOnlyFieldSx, { flexGrow: 1, mx: 2 }]}
          />
          {/* Right: Animation/date/rate/unit controls and animation buttons */}
          <Box sx={{ flex: 'none', display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={newValue => {
                  setFromDate(newValue);
                  if (newValue && newValue.isValid()) {
                    localStorage.setItem('visualUniverse_fromDate', newValue.format('YYYY-MM-DD'));
                  }
                  if (animationPhase !== 'idle') {
                    setAnimationPhase('idle');
                  }
                }}
                format="YYYY-MM-DD"
                slotProps={{ textField: { size: 'small', sx: { width: 155 }, variant: 'outlined' } }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={newValue => {
                  setToDate(newValue);
                  if (newValue && newValue.isValid()) {
                    localStorage.setItem('visualUniverse_toDate', newValue.format('YYYY-MM-DD'));
                  }
                  if (animationPhase !== 'idle') {
                    setAnimationPhase('idle');
                  }
                }}
                format="YYYY-MM-DD"
                minDate={fromDate}
                slotProps={{ textField: { size: 'small', sx: { width: 155 }, variant: 'outlined' } }}
              />
            </LocalizationProvider>
            <Typography variant="body2" sx={{ fontWeight: 'bold', ml: 2 }}>Rate of change (s):</Typography>
            <FormControl size="small">
              <Select value={rate} onChange={(e) => setRate(e.target.value)} sx={{ width: 60 }}>
                <MenuItem value={1}>1</MenuItem>
                <MenuItem value={2}>2</MenuItem>
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" sx={{ fontWeight: 'bold', ml: 2 }}>Change Unit:</Typography>
            <FormControl size="small">
              <Select value={unit} onChange={(e) => setUnit(e.target.value)} sx={{ width: 120 }}>
                <MenuItem value={2}>2 hours</MenuItem>
                <MenuItem value={8}>8 hours</MenuItem>
                <MenuItem value={16}>16 hours</MenuItem>
                <MenuItem value={24}>24 hours</MenuItem>
                <MenuItem value={168}>168 hours</MenuItem>
              </Select>
            </FormControl>
            <Button
              onClick={handleStart}
              disabled={!isStartEnabled || animationPhase !== 'idle'}
              sx={{ backgroundColor: 'primary.main', color: 'white', minWidth: 80, mx: 1 }}
            >
              START
            </Button>
            <Button
              onClick={handlePauseContinue}
              disabled={animationPhase === 'idle' || (animationPhase === 'paused' && !isStartEnabled)}
              sx={{ backgroundColor: animationPhase === 'paused' ? 'success.main' : 'warning.main', color: 'white', minWidth: 80, mr: 3 }}
            >
              {animationPhase === 'paused' ? 'CONTINUE' : 'PAUSE'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

VisualUniverseDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialDate: PropTypes.string,
};

export default VisualUniverseDialog;
